import { NetworkStatus } from "@apollo/client";
import { useEffect, useMemo } from "react";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import useTypedContext from "hooks/useTypedContext";
import { SchedulingItem } from "shared/Stack/Scheduling/types";
import { Stack } from "types/generated";

import { StackSchedulingContextApi } from "../Context";
import { GET_STACK_SCHEDULING } from "./gql";

type ResponseData = {
  stack: {
    state: Stack["state"];
    blocked: Stack["blocked"];
    integrations: Stack["integrations"];
    scheduledDeletes: Stack["scheduledDeletes"];
    scheduledTasks: Stack["scheduledTasks"];
    scheduledRuns: Stack["scheduledRuns"];
    workerPool: Stack["workerPool"];
  };
};

const useGetStackScheduling = (stackId: string) => {
  const { setDriftDetectionExist } = useTypedContext(StackSchedulingContextApi);

  const { data, error, loading, networkStatus } = usePolledQuery<ResponseData>(
    GET_STACK_SCHEDULING,
    {
      variables: {
        stack: stackId,
      },
      pollingGroup: PollingIntervalGroups.SingleEntities,
    }
  );

  const items = useMemo<SchedulingItem[]>(() => {
    return [
      ...((data?.stack?.integrations?.driftDetection && [data.stack.integrations.driftDetection]) ||
        []),
      ...(data?.stack?.scheduledDeletes || []),
      ...(data?.stack?.scheduledTasks || []),
      ...(data?.stack?.scheduledRuns || []),
    ];
  }, [data]);

  useEffect(() => {
    setDriftDetectionExist(!!data?.stack?.integrations?.driftDetection);
  }, [data?.stack?.integrations?.driftDetection, setDriftDetectionExist]);

  return {
    items,
    isStackBlocked: data?.stack?.blocked || false,
    hasPrivateWorkerPool: !!data?.stack?.workerPool,
    state: data?.stack?.state || undefined,
    isPageLoading: loading && !data?.stack && networkStatus === NetworkStatus.loading,
    isPageEmpty: !error && items.length === 0,
    error,
  };
};

export default useGetStackScheduling;
